import { useMemo } from 'react';
import type { ColumnDef, VisualizationConfig } from '@deckspace/types';
import { prepKanbanData, type KanbanMapping } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: KanbanMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

interface KanbanCard {
  id: string;
  title: string;
  subtitle?: string;
  rowIndex: number;
}

/** Common status values that get priority ordering. */
const STATUS_PRIORITY: Record<string, number> = {
  todo: 0,
  'to do': 0,
  'to-do': 0,
  backlog: 0,
  pending: 1,
  'in progress': 2,
  in_progress: 2,
  'in-progress': 2,
  active: 2,
  review: 3,
  'in review': 3,
  in_review: 3,
  done: 4,
  complete: 4,
  completed: 4,
  closed: 5,
  archived: 5,
};

function getColumnOrder(name: string): number {
  const normalized = name.toLowerCase().trim();
  return STATUS_PRIORITY[normalized] ?? 100;
}

export function Kanban({ rows, columnDefs: _columnDefs, mapping, config: _config }: Props) {
  const prep = useMemo(() => prepKanbanData(rows, mapping), [rows, mapping]);

  const { sortedColumnNames, cardsByColumn } = useMemo(() => {
    if (prep.rows.length === 0) {
      return { sortedColumnNames: [] as string[], cardsByColumn: new Map<string, KanbanCard[]>() };
    }

    const grouped = new Map<string, KanbanCard[]>();
    const columnOrder: string[] = [];

    for (let i = 0; i < prep.rows.length; i++) {
      const row = prep.rows[i];
      if (!row) continue;
      const colValue = String(row[prep.columnField] ?? '').trim();
      const title = String(row[prep.cardField] ?? '');
      const subtitle = prep.subtitleField ? String(row[prep.subtitleField] ?? '') : undefined;

      if (!grouped.has(colValue)) {
        grouped.set(colValue, []);
        columnOrder.push(colValue);
      }

      grouped.get(colValue)?.push({
        id: `row-${i}`,
        title,
        subtitle: subtitle || undefined,
        rowIndex: i,
      });
    }

    const sorted = columnOrder.sort((a, b) => {
      const aPriority = getColumnOrder(a);
      const bPriority = getColumnOrder(b);
      if (aPriority !== bPriority) return aPriority - bPriority;
      return 0;
    });

    return { sortedColumnNames: sorted, cardsByColumn: grouped };
  }, [prep]);

  if (rows.length === 0 || sortedColumnNames.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No data available
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-x-auto gap-3 p-3">
      {sortedColumnNames.map((colName) => {
        const cards = cardsByColumn.get(colName) ?? [];
        return (
          <div
            key={colName}
            className="flex flex-col min-w-[200px] max-w-[280px] flex-1 bg-muted/30 rounded-lg"
          >
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border">
              <h3 className="text-sm font-medium text-foreground truncate">{colName}</h3>
              <span className="text-xs text-muted-foreground bg-muted rounded-full px-1.5 py-0.5 tabular-nums">
                {cards.length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className="bg-card border border-border rounded-md px-3 py-2.5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <p className="text-sm font-medium text-foreground leading-snug">{card.title}</p>
                  {card.subtitle && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">{card.subtitle}</p>
                  )}
                </div>
              ))}
              {cards.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-4 italic">
                  No items
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
